import { Dispatch } from 'redux';
import toast from 'react-hot-toast';
import { Services } from '../../types/services';
import {
  ConfigurationReadResponseItem,
  AddOrUpdateConfigurationParams,
  RemoveConfigurationParams,
} from '../../v2/types';

const FETCH_CONFIGS_REQUEST = 'FETCH_UNIVERSAL_CONFIGS_REQUEST';
const SET_CONFIGS = 'SET_UNIVERSAL_CONFIGS';
const CONFIGS_ERROR = 'UNIVERSAL_CONFIGS_ERROR';

interface Action {
  type: string;
  payload?: unknown;
}

interface State {
  configs: ConfigurationReadResponseItem[];
  loading: boolean;
  error: string;
}

const initialState: State = { configs: [], loading: false, error: '' };

export const reducer = (state = initialState, action: Action): State => {
  switch (action.type) {
    case FETCH_CONFIGS_REQUEST:
      return { ...state, loading: true, error: '' };
    case SET_CONFIGS:
      return {
        ...state,
        loading: false,
        configs: action.payload as ConfigurationReadResponseItem[],
      };
    case CONFIGS_ERROR:
      return { ...state, loading: false, error: action.payload as string };
    default:
      return state;
  }
};

export const actions = (services: Services) => {
  const axiosCall = services.axios.Axios();

  // GET — fetch all keys that have a value at the given instance scope.
  // Response is a bare JSON array (no envelope): res.data is ConfigurationReadResponseItem[].
  const fetchUniversalConfigs = (instanceId: string) => {
    return async (dispatch: Dispatch) => {
      dispatch({ type: FETCH_CONFIGS_REQUEST });
      try {
        const res = await axiosCall.get('configurations/ReadUniversalConfigs', {
          params: { instanceId, expandMode: 'UpAndDown' },
        });
        dispatch({
          type: SET_CONFIGS,
          payload: (res as { data: ConfigurationReadResponseItem[] }).data,
        });
      } catch (err: unknown) {
        const msg = (err as Error).message;
        dispatch({ type: CONFIGS_ERROR, payload: msg });
        toast.error(`Error loading configurations: ${msg}`, { duration: 5000 });
      }
    };
  };

  // POST — add or update a config. instanceId is passed by the caller (not shown in UI).
  const addOrUpdateUniversalConfig = (
    params: AddOrUpdateConfigurationParams,
    instanceId: string,
  ) => {
    return async (dispatch: Dispatch) => {
      try {
        await axiosCall.post('configurations/AddOrUpdateUniversalConfigs', null, { params });
        await fetchUniversalConfigs(instanceId)(dispatch);
      } catch (err: unknown) {
        toast.error(`Error saving configuration: ${(err as Error).message}`, { duration: 5000 });
        throw err;
      }
    };
  };

  // POST — soft-delete via RemoveUniversalConfigs (backend now sets IsDeleted=true).
  const removeUniversalConfig = (params: RemoveConfigurationParams, instanceId: string) => {
    return async (dispatch: Dispatch) => {
      try {
        await axiosCall.post('configurations/RemoveUniversalConfigs', null, { params });
        await fetchUniversalConfigs(instanceId)(dispatch);
      } catch (err: unknown) {
        toast.error(`Error deleting configuration: ${(err as Error).message}`, { duration: 5000 });
        throw err;
      }
    };
  };

  return { fetchUniversalConfigs, addOrUpdateUniversalConfig, removeUniversalConfig };
};
